import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useSidebar } from "../contexts/SidebarContext";
import { supabase } from "../supabase";
import toast from "react-hot-toast";
import RoleBasedAccess from "./RoleBasedAccess";

const DashboardLayout = ({ children, pageTitle }) => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { isSidebarCollapsed, setIsSidebarCollapsed } = useSidebar();
  const [theme, setTheme] = useState(localStorage.getItem("theme") || "light");

  useEffect(() => {
    if (theme === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
    localStorage.setItem("theme", theme);
  }, [theme]);

  const handleLogout = async () => {
    const formData = localStorage.getItem('formData');
    if (formData) {
      if (window.confirm("You have unsaved changes in the File Opening form. Are you sure you want to logout? Your changes will be lost.")) {
        localStorage.removeItem('formData');
      } else {
        return;
      }
    }

    const { error } = await supabase.auth.signOut();
    if (error) {
      toast.error(error.message);
    } else {
      toast.success("Logged out successfully!");
      setTimeout(() => {
        navigate("/");
      }, 1000);
    }
  };

  const navigation = [
    { name: "Dashboard", href: "/dashboard", icon: "🏠" },
    { name: "Create File Opening", href: "/file-opening/create", icon: "📝" },
    { name: "File Openings", href: "/file-openings", icon: "📂" },
    { name: "File Opening Register", href: "/file-opening-register", icon: "📋", adminOnly: true },
    { name: "Cash Register", href: "/cash-register", icon: "📠", adminOnly: true },
    { name: "Ledger", href: "/ledger", icon: "💰", adminOnly: true },
    // Add more navigation links here
  ];

  const isActive = (path) => {
    return location.pathname === path;
  };

  const toggleTheme = () => {
    setTheme(theme === "light" ? "dark" : "light");
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className={`fixed inset-y-0 left-0 bg-white dark:bg-gray-800 shadow-lg transition-all duration-300 ${isSidebarCollapsed ? 'w-20' : 'w-64'}`}>
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            {!isSidebarCollapsed && <h1 className="text-xl font-bold text-gray-800 dark:text-white">SKA</h1>}
            <button onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)} className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"></path></svg>
            </button>
          </div>

          <nav className="flex-1 p-4 space-y-2">
            {navigation.map((item) => (
              item.adminOnly ? (
                <RoleBasedAccess key={item.name} requiredRole={["admin", "superadmin"]}>
                  <Link
                    to={item.href}
                    className={`flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${isActive(item.href)
                      ? "bg-blue-100 text-blue-700 dark:bg-gray-700 dark:text-blue-300"
                      : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white"
                      }`}
                  >
                    <span className="mr-3 text-lg">{item.icon}</span>
                    {!isSidebarCollapsed && item.name}
                  </Link>
                </RoleBasedAccess>
              ) : (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${isActive(item.href)
                    ? "bg-blue-100 text-blue-700 dark:bg-gray-700 dark:text-blue-300"
                    : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white"
                    }`}
                >
                  <span className="mr-3 text-lg">{item.icon}</span>
                  {!isSidebarCollapsed && item.name}
                </Link>
              )
            ))}
            <button
              onClick={handleLogout}
              className={`flex items-center w-full px-4 py-3 text-sm font-medium text-left text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white`}
            >
              <span className="mr-3 text-lg">🚪</span>
              {!isSidebarCollapsed && 'Logout'}
            </button>
          </nav>

          <div className="p-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-3">
              {!isSidebarCollapsed && (
                <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-bold">
                  {user?.email?.[0].toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                {!isSidebarCollapsed && <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user?.email}</p>}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={`transition-all duration-300 ${isSidebarCollapsed ? 'pl-20' : 'pl-64'}`}>
        <header className="bg-white dark:bg-gray-800 shadow-sm">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{pageTitle}</h1>
              <div className="flex">
                <p className="mr-2 dark:text-white">{"Switch Theme"}</p>
                <button onClick={toggleTheme} className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white">
                  {theme === 'light' ? '🌙' : '☀️'}
                </button>
              </div>
            </div>
          </div>
        </header>
        <main className="p-6">{children}</main>
      </div>
    </div>
  );
};

export default DashboardLayout;
